"use client";

import { m as motion } from "framer-motion";
import type { ReactNode, ComponentType } from "react";
import {
  CalendarCheck,
  MessagesSquare,
  FileText,
  ArrowRight,
  ArrowDown,
} from "lucide-react";

interface ContattiStepsProps {
  isItalian: boolean;
}

type Step = {
  icon: ComponentType<{ className?: string }>;
  title: string;
  back: ReactNode;
};

const HL = "font-medium text-primary";
const G = "hsl(var(--primary))";

/** Grafica line-art sotto il titolo di ogni step (calendario / chat / documento). */
function StepGraphic({ index }: { index: number }) {
  if (index === 0) {
    return (
      <svg viewBox="0 0 132 56" className="h-auto w-32" fill="none" aria-hidden="true">
        <rect x="8" y="10" width="116" height="42" rx="6" stroke={G} strokeWidth="1.4" strokeOpacity="0.5" />
        <line x1="8" y1="23" x2="124" y2="23" stroke={G} strokeWidth="1.4" strokeOpacity="0.45" />
        <line x1="34" y1="4" x2="34" y2="14" stroke={G} strokeWidth="1.8" strokeOpacity="0.7" strokeLinecap="round" />
        <line x1="98" y1="4" x2="98" y2="14" stroke={G} strokeWidth="1.8" strokeOpacity="0.7" strokeLinecap="round" />
        {[0, 1, 2, 3].map((c) => (
          <rect key={c} x={18 + c * 25} y={32} width="14" height="11" rx="2" stroke={G} strokeWidth="1" strokeOpacity="0.3" />
        ))}
        <rect x={43} y={32} width="14" height="11" rx="2" fill={G} fillOpacity="0.55" />
      </svg>
    );
  }
  if (index === 1) {
    return (
      <svg viewBox="0 0 132 56" className="h-auto w-32" fill="none" aria-hidden="true">
        <path d="M10 8 H78 A7 7 0 0 1 85 15 V31 A7 7 0 0 1 78 38 H34 L22 48 L25 38 H10 A7 7 0 0 1 3 31 V15 A7 7 0 0 1 10 8 Z" stroke={G} strokeWidth="1.4" strokeOpacity="0.55" />
        {[24, 40, 56].map((cx) => (
          <circle key={cx} cx={cx} cy={23} r="2.6" fill={G} fillOpacity="0.6" />
        ))}
        <path d="M122 20 H96 A6 6 0 0 0 90 26 V38 A6 6 0 0 0 96 44 H112 L120 52 L118 44 H122 A6 6 0 0 0 128 38 V26 A6 6 0 0 0 122 20 Z" stroke={G} strokeWidth="1.4" strokeOpacity="0.35" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 132 56" className="h-auto w-32" fill="none" aria-hidden="true">
      <rect x="14" y="6" width="104" height="44" rx="6" stroke={G} strokeWidth="1.4" strokeOpacity="0.5" />
      <line x1="26" y1="18" x2="86" y2="18" stroke={G} strokeWidth="2" strokeOpacity="0.5" strokeLinecap="round" />
      <line x1="26" y1="27" x2="106" y2="27" stroke={G} strokeWidth="1.6" strokeOpacity="0.3" strokeLinecap="round" />
      <line x1="26" y1="35" x2="94" y2="35" stroke={G} strokeWidth="1.6" strokeOpacity="0.3" strokeLinecap="round" />
      <path d="M78 40 L86 47 L102 31" stroke={G} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * "Cosa succede dopo": i 3 step dopo il contatto. Su desktop sono schede con
 * FLIP 3D lento all'hover: fronte = icona + numero (che si illumina) + titolo
 * grande; retro = descrizione con parole chiave in ciano, riga per riga. Su
 * mobile/touch (niente hover) la descrizione e' gia' visibile sul fronte.
 */
export function ContattiSteps({ isItalian }: ContattiStepsProps) {
  const steps: Step[] = isItalian
    ? [
        {
          icon: CalendarCheck,
          title: "Prenoti in 30 secondi",
          back: (
            <>
              Scegli lo <span className={HL}>slot che ti torna comodo</span> dal
              calendario.
              <br />
              Nessun account, nessun modulo infinito.
            </>
          ),
        },
        {
          icon: MessagesSquare,
          title: "Call di 30 minuti",
          back: (
            <>
              Mi racconti dove sei e cosa ti serve.
              <br />
              Ti dico subito <span className={HL}>se e come posso aiutarti</span>,
              senza giri di parole.
            </>
          ),
        },
        {
          icon: FileText,
          title: "Proposta chiara",
          back: (
            <>
              Ricevi una proposta con{" "}
              <span className={HL}>tempi e prezzi chiari</span>.
              <br />
              Decidi con calma, senza pressioni.
            </>
          ),
        },
      ]
    : [
        {
          icon: CalendarCheck,
          title: "Book in 30 seconds",
          back: (
            <>
              Pick the <span className={HL}>slot that works for you</span> from
              the calendar.
              <br />
              No account, no endless form.
            </>
          ),
        },
        {
          icon: MessagesSquare,
          title: "30-minute call",
          back: (
            <>
              You tell me where you are and what you need.
              <br />
              I tell you right away <span className={HL}>if and how I can help</span>,
              no fluff.
            </>
          ),
        },
        {
          icon: FileText,
          title: "Clear proposal",
          back: (
            <>
              You get a proposal with{" "}
              <span className={HL}>clear timelines and prices</span>.
              <br />
              Decide calmly, no pressure.
            </>
          ),
        },
      ];

  return (
    <section className="section-padding pb-24 md:pb-32">
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="mb-10 max-w-2xl text-left"
        >
          <span className="font-mono text-label text-primary mb-3 block">
            {isItalian ? "Come funziona" : "How it works"}
          </span>
          <h2 className="font-display text-h2 font-bold">
            {isItalian ? "Cosa succede dopo" : "What happens next"}
          </h2>
        </motion.div>

        <div className="grid max-w-5xl items-stretch gap-4 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
          {steps.map((s, i) => (
            <div key={s.title} className="contents">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="group h-full [perspective:1400px]"
              >
                <div className="relative h-full min-h-[14rem] transition-transform duration-700 ease-out [transform-style:preserve-3d] md:group-hover:[transform:rotateY(180deg)]">
                  {/* FRONTE: solo icona + numero + titolo grande (desktop) */}
                  <div className="flex h-full flex-col rounded-xl border border-[hsl(var(--border))] bg-surface/40 p-6 [backface-visibility:hidden]">
                    <div className="mb-4 flex items-center gap-3">
                      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 transition-shadow group-hover:shadow-[0_0_20px_hsl(var(--primary)/0.35)]">
                        <s.icon className="h-5 w-5 text-primary" />
                      </span>
                      <span className="font-mono text-lg font-bold text-muted-foreground transition-all duration-300 group-hover:scale-110 group-hover:text-primary">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="font-display text-xl font-semibold md:text-2xl">
                      {s.title}
                    </h3>
                    {/* Grafica sotto il titolo (desktop): riempie il fronte */}
                    <div className="mt-auto hidden pt-6 md:block">
                      <StepGraphic index={i} />
                    </div>
                    {/* Mobile/reduced-motion: descrizione visibile senza flip */}
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:hidden motion-reduce:md:block">
                      {s.back}
                    </p>
                  </div>

                  {/* RETRO: descrizione con keyword (visibile al flip su desktop) */}
                  <div className="absolute inset-0 hidden flex-col justify-center rounded-xl border border-primary/30 bg-surface p-6 [backface-visibility:hidden] [transform:rotateY(180deg)] md:flex">
                    <span className="mb-2 font-mono text-xs uppercase tracking-[0.14em] text-primary/80">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-sm leading-relaxed text-foreground/85">
                      {s.back}
                    </p>
                  </div>
                </div>
              </motion.div>

              {i < steps.length - 1 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.1 + 0.2 }}
                  className="flex items-center justify-center text-primary"
                  aria-hidden
                >
                  <ArrowRight className="hidden h-5 w-5 md:block" />
                  <ArrowDown className="h-5 w-5 md:hidden" />
                </motion.div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
